/**
 * Client-setting-driven role permission check (shared).
 *
 * A family of client settings hold a PIPE_SEPARATED list of system roles allowed
 * to do something (`levelling_framework_admin`, `levelling_anchor_roles_admin`,
 * `atlas_flyout_permission_groups`, …). The user is permitted when they belong to
 * at least one of the listed permission groups.
 *
 * Faithful extraction of rm-portal's `loginState.checkForPermission` (main.js) so
 * both portals share one implementation — rm-portal-beta previously inlined a
 * duplicate of this logic per component.
 *
 * NOTE: this is a UX gate only. The server re-checks the same setting on every
 * write (see e.g. DocumentService.canGovernAnchors); never treat a `true` here as
 * authorisation.
 *
 * @param {Object} user            the logged-in user ({ settings, permission_groups })
 * @param {string} setting         client setting name holding the pipe-separated roles
 * @param {Object} [options]
 * @param {string[]} [options.fallbackRoles]  roles to use when the setting has no value
 *                                            (mirrors the backend's default)
 * @returns {boolean}
 */
export function hasRolePermission(user, setting, options) {
  if (!user || !setting) return false;

  const match = (user.settings || []).find((x) => x && x.setting === setting && x.value);
  const raw = match ? String(match.value) : '';
  const required = raw
    ? raw.split('|').map((r) => r.trim()).filter(Boolean)
    : ((options && options.fallbackRoles) || []);
  if (!required.length) return false;

  // "all" as the only entry means everyone — the convention used by
  // DocumentService.hasPermission for the job-catalogue settings.
  if (required.length === 1 && required[0].toLowerCase() === 'all') return true;

  const groups = user.permission_groups || [];
  return required.some((r) => groups.some((pg) => pg === r));
}

/**
 * Read a BOOL-ish client setting. Returns true only for an explicit 'true'.
 * @param {Object} user
 * @param {string} setting
 * @returns {boolean}
 */
export function hasClientSetting(user, setting) {
  if (!user || !setting) return false;
  const match = (user.settings || []).find((x) => x && x.setting === setting);
  return !!match && String(match.value) === 'true';
}
